import BodyText from '@lyra/core/components/BodyText'
import Button from '@lyra/core/components/Button'
import Checkbox from '@lyra/core/components/Checkbox'
import DataLabelRow from '@lyra/core/components/DataLabelRow'
import BigNumberInput from '@lyra/core/components/Input/BigNumberInput'
import Section from '@lyra/core/components/Section'
import { WEI_DECIMALS } from '@lyra/core/constants/contracts'
import { bigNumberToNumberUNSAFE } from '@lyra/core/utils/bigNumberToNumberUNSAFE'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import { TokenId } from '@lyra/web/constants/tokens'
import { TransactionOptions } from '@lyra/web/constants/transactions'
import useLyraFundingBalances from '@lyra/web/hooks/useLyraFundingBalances'
import useStakingRewards from '@lyra/web/hooks/useStakingRewards'
import useTransaction from '@lyra/web/hooks/useTransaction'
import { formatTokenBalance } from '@lyra/web/utils/tokens'
import { useCallback, useState } from 'react'
import { XStack } from 'tamagui'

import WalletTransactionModal from '../common/WalletTransactionModal'

type Props = {
  isUnstake?: boolean
  onClose: () => void
}

export default function DrvStakeModal({ isUnstake, onClose }: Props) {
  const { fundingBalances, mutate } = useLyraFundingBalances()
  const drvBalance = fundingBalances.DRV.balanceBn
  const stDrvBalance = fundingBalances.STDRV.balanceBn

  const { stake, unstake } = useTransaction()
  const [isInstantUnstake, setIsInstantUnstake] = useState(false)
  const [amountInput, setAmountInput] = useState(BigInt(0))

  const isEmptyInput = amountInput === BigInt(0)
  const penaltyAmount = amountInput / BigInt(5)

  const { data, isLoading } = useStakingRewards()

  const handlePressUnstake = useCallback(
    async (options: TransactionOptions) => {
      await unstake(amountInput, isInstantUnstake, options)
      await mutate()
    },
    [amountInput, isInstantUnstake, mutate, unstake]
  )

  const handlePressStake = useCallback(
    async (options: TransactionOptions) => {
      await stake(amountInput, options)
      await mutate()
    },
    [amountInput, mutate, stake]
  )

  const isStakeDisabled = isEmptyInput || amountInput > drvBalance
  const isUnstakeDisabled = isEmptyInput || amountInput > stDrvBalance

  const stakingApy = data?.stakingApy.STDRV ?? 0

  return (
    <WalletTransactionModal
      onClose={onClose}
      reviewTitle={isUnstake ? 'Unstake' : 'Stake'}
      transaction={isUnstake ? handlePressUnstake : handlePressStake}
      loggerAction={isUnstake ? 'unstake' : 'stake'}
      isDisabled={isUnstake ? isUnstakeDisabled : isStakeDisabled}
      reviewButtonLabel={isUnstake ? 'Unstake' : 'Stake'}
      reviewContent={
        <>
          <Section.YStack>
            <BigNumberInput
              size="lg"
              label="Amount"
              formatValue={(v) =>
                `${formatNumber(bigNumberToNumberUNSAFE(v, WEI_DECIMALS))} ${
                  isUnstake ? 'stDRV' : 'DRV'
                }`
              }
              decimals={WEI_DECIMALS}
              value={amountInput}
              onChangeValue={setAmountInput}
              rightContent={
                <Button
                  isTransparent
                  size="sm"
                  label="Max"
                  onPress={() => setAmountInput(isUnstake ? stDrvBalance : drvBalance)}
                />
              }
            />
          </Section.YStack>
          {isUnstake ? (
            <Section.XStack>
              <XStack
                gap="$1"
                onPress={() => setIsInstantUnstake(!isInstantUnstake)}
                cursor="pointer"
                alignItems="center"
              >
                <Checkbox isChecked={isInstantUnstake} />
                <BodyText color="secondary">Unstake Instantly</BodyText>
              </XStack>
            </Section.XStack>
          ) : null}
          {isUnstake ? (
            <Section.YStack>
              <DataLabelRow
                label="Balance"
                value={formatTokenBalance(stDrvBalance, TokenId.STDRV)}
              />
              <DataLabelRow
                label="Unstake Period"
                value={isInstantUnstake ? '0 Days' : '28 Days'}
              />
              {isInstantUnstake ? (
                <DataLabelRow
                  label="Unstake Penalty"
                  color="red"
                  value={`-${formatTokenBalance(penaltyAmount, TokenId.DRV)} (20%)`}
                />
              ) : null}
            </Section.YStack>
          ) : (
            <Section.YStack>
              <DataLabelRow label="Balance" value={formatTokenBalance(drvBalance, TokenId.DRV)} />
              <DataLabelRow
                label="Staking APY"
                color={stakingApy > 0 ? 'green' : 'primary'}
                value={isLoading ? '...' : formatPercentage(stakingApy, { showSign: true })}
              />
              <DataLabelRow label="Unstake Period" value="28 Days" />
            </Section.YStack>
          )}
        </>
      }
    />
  )
}
